import { useRef, useState, useEffect } from 'react';
import { Box, Container, Grid, Typography, Button, Stack, Chip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CTABanner from '../components/common/CTABanner';
import StatsCounter from '../components/common/StatsCounter';
import { serviceOverview } from '../data/services';
import { projects as staticProjects } from '../data/projects';
import { useProjects, usePageContent, useSettings } from '../hooks/usePublicApi';
import { getProjectImageUrl, getImageUrl } from '../utils/imageUrl';

// ── Colors ────────────────────────────────────────────────────────────────────
const navy = '#0F1D2E';
const navyLight = '#1B2D42';
const gold = '#D1BC8A';
const goldDark = '#B8A06E';

// ── Marquee items ─────────────────────────────────────────────────────────────
const MARQUEE_ITEMS = [
  'Neubau', 'Umbau', 'Sanierung', 'Innenausbau', 'München', 'Qualität',
  'Trockenbau', 'Elektroinstallation', 'Seit 2009', 'Meisterbetrieb',
];

// ── Static data ───────────────────────────────────────────────────────────────
const defaultProcessSteps = [
  { step: '01', title: 'Erstberatung', desc: 'Kostenlose Beratung und Besichtigung vor Ort.' },
  { step: '02', title: 'Planung', desc: 'Detaillierte Planung und transparentes Angebot.' },
  { step: '03', title: 'Umsetzung', desc: 'Professionelle Ausführung mit Qualitätsgarantie.' },
  { step: '04', title: 'Übergabe', desc: 'Saubere Übergabe — erst wenn Sie zufrieden sind.' },
];
const defaultAdvantages = [
  'Alles aus einer Hand', 'Termingerechte Fertigstellung',
  'Transparente Preise', 'Qualifizierte Fachkräfte',
  'Kostenlose Erstberatung', 'Regionale Verankerung',
];

// ── Animation variants ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: (d = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.65, delay: d, ease: [0.22, 1, 0.36, 1] } }),
};
const fadeLeft = {
  hidden: { opacity: 0, x: -36 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};
const fadeRight = {
  hidden: { opacity: 0, x: 36 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function InView({ children, variants = fadeUp, delay = 0, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-70px 0px' });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'}
      variants={variants} custom={delay} style={{ height: '100%', ...style }}>
      {children}
    </motion.div>
  );
}

// Blueprint-style grid overlay
function Blueprint({ opacity = 0.06 }) {
  return (
    <Box sx={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      backgroundImage: `
        linear-gradient(rgba(255,255,255,${opacity}) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,${opacity}) 1px, transparent 1px)
      `,
      backgroundSize: '36px 36px',
    }} />
  );
}

// Category label lookup
const catLabels = { neubau: 'Neubau', umbau: 'Umbau', sanierung: 'Sanierung' };

// ══════════════════════════════════════════════════════════════════════════════
// HERO — fullscreen cycling project showcase
// ══════════════════════════════════════════════════════════════════════════════
function HeroSection({ projects, heroContent, heroStats, satisfaction }) {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

  const displayProjects = projects.slice(0, 5);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (displayProjects.length === 0) return;
    const t = setInterval(() => setActiveIdx(i => (i + 1) % displayProjects.length), 4500);
    return () => clearInterval(t);
  }, [displayProjects.length]);

  const activeProject = displayProjects[activeIdx] || null;
  const activeImageUrl = activeProject ? getProjectImageUrl(activeProject) : null;

  return (
    <Box ref={heroRef} sx={{ position: 'relative', overflow: 'hidden', flex: 1, display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, bgcolor: navy }}>

      {/* ── Ambient blurred background (shifts with active project) ── */}
      <AnimatePresence>
        <motion.div
          key={activeIdx}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4 }}
          style={{ position: 'absolute', inset: '-15%', zIndex: 0, pointerEvents: 'none' }}
        >
          <Box sx={{
            position: 'absolute', inset: 0,
            background: activeImageUrl
              ? `url(${activeImageUrl}) center/cover no-repeat`
              : activeProject?.gradient || `linear-gradient(145deg, ${navy} 0%, ${navyLight} 100%)`,
            filter: 'blur(90px)',
            opacity: 0.22,
            transform: 'scale(1.3)',
          }} />
        </motion.div>
      </AnimatePresence>

      {/* Base dark gradient */}
      <Box sx={{
        position: 'absolute', inset: 0, zIndex: 0,
        background: `linear-gradient(145deg, ${navy} 0%, #0D1A2A 60%, ${navy} 100%)`,
      }} />

      {/* Blueprint dot grid */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1,
        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '28px 28px',
      }} />

      {/* Gold top accent */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, zIndex: 4,
        background: `linear-gradient(90deg, transparent 0%, ${gold} 30%, #E8DEC3 60%, ${gold} 100%)`, opacity: 0.9 }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, pt: { xs: '82px', md: 9 }, pb: { xs: 4, md: 7 } }}>
        <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">

          {/* ── Image showcase (visible on mobile too — shown above text) ── */}
          <Grid size={{ xs: 12, md: 6 }} sx={{ order: { xs: -1, md: 0 } }}>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.85, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box sx={{ position: 'relative' }}>
                {/* Main cycling card */}
                <Box sx={{
                  borderRadius: '24px', overflow: 'hidden',
                  height: { xs: 220, sm: 320, md: 480 },
                  position: 'relative',
                  boxShadow: '0 32px 80px rgba(0,0,0,0.55)',
                }}>
                  <AnimatePresence mode="sync">
                    <motion.div
                      key={activeIdx}
                      initial={{ opacity: 0, scale: 1.04 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
                      style={{ position: 'absolute', inset: 0 }}
                    >
                      <Box sx={{
                        width: '100%', height: '100%',
                        background: activeImageUrl
                          ? `url(${activeImageUrl}) center/cover no-repeat`
                          : activeProject?.gradient || `linear-gradient(135deg, ${navyLight} 0%, #2A4562 100%)`,
                      }} />
                    </motion.div>
                  </AnimatePresence>

                  {/* Blueprint overlay */}
                  <Blueprint opacity={0.04} />

                  {/* Bottom scrim + project info */}
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: 'linear-gradient(to top, rgba(10,18,32,0.92) 0%, transparent 100%)',
                    p: { xs: 2, md: 2.5 }, zIndex: 2 }}>
                    {activeProject && (
                      <>
                        <Chip
                          label={catLabels[activeProject.category] || activeProject.category}
                          size="small"
                          sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', backdropFilter: 'blur(6px)',
                            border: '1px solid rgba(255,255,255,0.2)', fontSize: '0.65rem', fontWeight: 700, height: 20, mb: 0.75 }}
                        />
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: '1rem', md: '1.15rem' }, lineHeight: 1.2, mb: 0.3 }}>
                          {activeProject.title}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                          {activeProject.location}{activeProject.year && ` · ${activeProject.year}`}
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Corner ticks */}
                  {[
                    { top: 12, left: 12, borderTop: '1.5px solid', borderLeft: '1.5px solid' },
                    { top: 12, right: 12, borderTop: '1.5px solid', borderRight: '1.5px solid' },
                  ].map((s, i) => (
                    <Box key={i} sx={{ position: 'absolute', width: 16, height: 16, borderColor: 'rgba(209,188,138,0.4)', zIndex: 3, ...s }} />
                  ))}
                </Box>

                {/* Navigation dots + counter */}
                {displayProjects.length > 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
                    {displayProjects.map((_, i) => (
                      <Box
                        key={i}
                        onClick={() => setActiveIdx(i)}
                        sx={{
                          height: 6, borderRadius: 3, cursor: 'pointer',
                          width: i === activeIdx ? 28 : 8,
                          bgcolor: i === activeIdx ? gold : 'rgba(255,255,255,0.22)',
                          transition: 'all 0.35s ease',
                          '&:hover': { bgcolor: i === activeIdx ? gold : 'rgba(255,255,255,0.45)' },
                        }}
                      />
                    ))}
                  </Box>
                )}

                {/* Floating satisfaction badge */}
                <Box sx={{
                  position: 'absolute', bottom: { xs: 52, md: 72 }, right: -16, zIndex: 4,
                  px: 2, py: 1.5, borderRadius: '16px',
                  background: 'rgba(15,29,46,0.82)', backdropFilter: 'blur(16px)',
                  border: `1px solid rgba(209,188,138,0.22)`,
                  boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
                  '@keyframes floatBadge': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-6px)' } },
                  animation: 'floatBadge 4s ease-in-out infinite',
                  display: { xs: 'none', sm: 'block' },
                }}>
                  <Typography sx={{ color: gold, fontWeight: 900, fontSize: '1.5rem', lineHeight: 1 }}>{satisfaction}%</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', mt: 0.3 }}>Zufriedenheit</Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>

          {/* ── Text column ── */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div style={{ y: contentY, opacity: contentOpacity }}>
              <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>

                {/* Company badge */}
                <motion.div variants={fadeUp} custom={0}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderRadius: 10, mb: 3,
                    background: 'rgba(209,188,138,0.1)', border: `1px solid rgba(209,188,138,0.28)` }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: gold, boxShadow: `0 0 8px ${gold}` }} />
                    <Typography sx={{ color: gold, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      {heroContent?.subtitle || 'Süd Ausbau GmbH'}
                    </Typography>
                  </Box>
                </motion.div>

                {/* Headline */}
                <motion.div variants={fadeUp} custom={0.06}>
                  <Typography component="h1" sx={{
                    color: '#fff', fontWeight: 900, lineHeight: 1.07,
                    fontSize: { xs: '2.4rem', sm: '2.9rem', md: '3.4rem', lg: '3.8rem' },
                    letterSpacing: '-0.03em', mb: 3,
                  }}>
                    {heroContent?.title
                      ? heroContent.title
                      : <>
                          Innenausbau mit<br />
                          <Box component="span" sx={{
                            background: `linear-gradient(90deg, ${gold} 0%, #EDE3C8 45%, ${gold} 100%)`,
                            backgroundSize: '200% auto',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                            '@keyframes shine': { from: { backgroundPosition: '0% center' }, to: { backgroundPosition: '200% center' } },
                            animation: 'shine 5s linear infinite',
                          }}>Qualität</Box> und<br />Leidenschaft
                        </>
                    }
                  </Typography>
                </motion.div>

                {/* Description */}
                <motion.div variants={fadeUp} custom={0.12}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: '1rem', lineHeight: 1.8, mb: 4, maxWidth: 460 }}>
                    {heroContent?.description || 'Ihr zuverlässiger Partner für Neubau, Umbau und Sanierung in München. Von der Planung bis zur schlüsselfertigen Übergabe.'}
                  </Typography>
                </motion.div>

                {/* Buttons */}
                <motion.div variants={fadeUp} custom={0.18}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button variant="contained" size="large" component={RouterLink} to="/anfrage" endIcon={<ArrowForwardIcon />}
                      sx={{
                        background: `linear-gradient(135deg, ${gold} 0%, ${goldDark} 100%)`,
                        color: navy, fontWeight: 800, px: 3.5, py: 1.5, borderRadius: '12px',
                        boxShadow: `0 10px 28px rgba(209,188,138,0.32)`,
                        '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 18px 44px rgba(209,188,138,0.42)` },
                        transition: 'all 0.3s ease',
                      }}>
                      {heroContent?.buttonText || 'Anfrage senden'}
                    </Button>
                    <Button variant="outlined" size="large" component={RouterLink} to="/referenzen"
                      sx={{
                        borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)',
                        fontWeight: 600, px: 3.5, py: 1.5, borderRadius: '12px',
                        '&:hover': { borderColor: `rgba(209,188,138,0.45)`, color: gold, transform: 'translateY(-3px)' },
                        transition: 'all 0.3s ease',
                      }}>
                      Unsere Projekte
                    </Button>
                  </Stack>
                </motion.div>

                {/* Stats */}
                <motion.div variants={fadeUp} custom={0.26}>
                  <Box sx={{ mt: 5, pt: 4, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                    <Stack direction="row" spacing={4}>
                      {(heroStats || []).map((stat) => (
                        <Box key={stat.label}>
                          <Typography sx={{ color: gold, fontWeight: 900, fontSize: '1.5rem', lineHeight: 1 }}>{stat.value}{stat.suffix}</Typography>
                          <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', mt: 0.4 }}>{stat.label}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </motion.div>

              </motion.div>
            </motion.div>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MARQUEE
// ══════════════════════════════════════════════════════════════════════════════
function MarqueeStrip({ items }) {
  const src = (items && items.length > 0) ? items : MARQUEE_ITEMS;
  const doubled = [...src, ...src];
  return (
    <Box sx={{ bgcolor: navy, borderTop: `1px solid rgba(209,188,138,0.1)`, borderBottom: `1px solid rgba(209,188,138,0.1)`, py: 1.4, overflow: 'hidden' }}>
      <Box sx={{
        display: 'flex', width: 'max-content', whiteSpace: 'nowrap',
        '@keyframes marquee': { from: { transform: 'translateX(0)' }, to: { transform: 'translateX(-50%)' } },
        animation: 'marquee 26s linear infinite',
      }}>
        {doubled.map((item, i) => (
          <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <Typography sx={{ color: 'rgba(209,188,138,0.55)', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.2em', px: 3 }}>
              {item}
            </Typography>
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(209,188,138,0.25)', flexShrink: 0 }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PROJECTS — hero card + 3-card row
// ══════════════════════════════════════════════════════════════════════════════
function ProjectTile({ project, height = 280, large = false }) {
  const [hovered, setHovered] = useState(false);
  const imageUrl = getProjectImageUrl(project);

  return (
    <Box
      component={RouterLink}
      to={`/referenzen/${project.slug || project.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'block', textDecoration: 'none',
        position: 'relative', overflow: 'hidden', borderRadius: '20px',
        height,
        background: imageUrl ? `url(${imageUrl}) center/cover no-repeat` : project.gradient,
        transition: 'transform 0.5s cubic-bezier(0.22,1,0.36,1), box-shadow 0.45s ease',
        boxShadow: hovered ? '0 28px 60px rgba(0,0,0,0.28)' : '0 6px 24px rgba(0,0,0,0.14)',
        transform: hovered ? 'scale(1.012)' : 'scale(1)',
        cursor: 'pointer',
      }}
    >
      <Blueprint opacity={0.045} />
      {/* Scrim */}
      <Box sx={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(to bottom, transparent 25%, rgba(0,0,0,${hovered ? 0.68 : 0.52}) 100%)`,
        transition: 'background 0.4s ease',
      }} />
      {/* Category */}
      <Chip label={catLabels[project.category] || project.category} size="small" sx={{
        position: 'absolute', top: 14, left: 14,
        bgcolor: 'rgba(255,255,255,0.13)', color: '#fff', backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.18)', fontWeight: 700, fontSize: '0.7rem',
      }} />
      {/* Arrow */}
      <Box sx={{
        position: 'absolute', top: 14, right: 14,
        width: 38, height: 38, borderRadius: '50%',
        background: `linear-gradient(135deg, ${gold} 0%, ${goldDark} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: hovered ? 1 : 0,
        transform: hovered ? 'scale(1) rotate(0)' : 'scale(0.5) rotate(-45deg)',
        transition: 'all 0.4s cubic-bezier(0.22,1,0.36,1)',
      }}>
        <ArrowForwardIcon sx={{ color: navy, fontSize: 17 }} />
      </Box>
      {/* Info */}
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: large ? 3 : 2.5 }}>
        <Typography sx={{
          color: '#fff', fontWeight: 800, lineHeight: 1.2, mb: 0.4,
          fontSize: large ? { xs: '1.2rem', md: '1.45rem' } : '1rem',
        }}>
          {project.title}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>
          {project.subtitle && `${project.subtitle} · `}{project.location}
        </Typography>
      </Box>
    </Box>
  );
}

function ProjectsSection({ projects }) {
  const p = projects.slice(0, 5);
  return (
    <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: '#F8F7F5' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <InView style={{ height: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: { xs: 4, md: 5 } }}>
            <Box>
              <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2em', mb: 0.75 }}>Referenzen</Typography>
              <Typography variant="h2" sx={{ color: navy, fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
                Ausgewählte<br />Projekte
              </Typography>
            </Box>
            <Button component={RouterLink} to="/referenzen" variant="outlined" endIcon={<ArrowForwardIcon />}
              sx={{ borderColor: 'rgba(15,29,46,0.16)', color: navy, fontWeight: 700, borderRadius: '10px', px: 2.5, py: 1,
                '&:hover': { borderColor: navy, bgcolor: navy, color: '#fff' } }}>
              Alle ansehen
            </Button>
          </Box>
        </InView>

        {p.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Projekte werden geladen…
            </Typography>
            <Button component={RouterLink} to="/referenzen" variant="outlined" endIcon={<ArrowForwardIcon />}
              sx={{ borderColor: 'rgba(15,29,46,0.16)', color: navy, fontWeight: 700, borderRadius: '10px' }}>
              Alle Referenzen ansehen
            </Button>
          </Box>
        ) : (
          <>
            {/* Row 1: big card + side card */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {p[0] && (
                <Grid size={{ xs: 12, md: 7 }}>
                  <InView variants={fadeLeft} style={{ height: 'auto' }}>
                    <ProjectTile project={p[0]} height={400} large />
                  </InView>
                </Grid>
              )}
              {p[1] && (
                <Grid size={{ xs: 12, md: 5 }}>
                  <InView variants={fadeRight} style={{ height: 'auto' }}>
                    <ProjectTile project={p[1]} height={400} />
                  </InView>
                </Grid>
              )}
            </Grid>

            {/* Row 2: 3 equal cards */}
            <Grid container spacing={2}>
              {p.slice(2, 5).map((project, i) => (
                <Grid size={{ xs: 12, sm: 4 }} key={project.id}>
                  <InView variants={fadeUp} delay={i * 0.08} style={{ height: 'auto' }}>
                    <ProjectTile project={project} height={230} />
                  </InView>
                </Grid>
              ))}
            </Grid>

            {/* "View all" link */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                component={RouterLink} to="/referenzen" size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{ color: navy, fontWeight: 700, fontSize: '0.95rem',
                  '&:hover': { color: goldDark, bgcolor: 'transparent' } }}
              >
                Alle Referenzen ansehen
              </Button>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SERVICES
// ══════════════════════════════════════════════════════════════════════════════
const svcGradients = [
  `linear-gradient(140deg, ${navy} 0%, #2A4562 100%)`,
  `linear-gradient(140deg, #1A4828 0%, #2D7042 100%)`,
  `linear-gradient(140deg, #2D1B4F 0%, #503080 100%)`,
];

function ServiceCard({ service, gradient }) {
  const [hov, setHov] = useState(false);
  const Icon = service.icon;
  return (
    <Box
      component={RouterLink} to={service.path}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      sx={{
        display: 'flex', flexDirection: 'column', textDecoration: 'none',
        borderRadius: '22px', overflow: 'hidden', background: gradient,
        position: 'relative', minHeight: 320,
        transition: 'transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s ease',
        transform: hov ? 'translateY(-7px)' : 'translateY(0)',
        boxShadow: hov ? '0 24px 56px rgba(0,0,0,0.2)' : '0 6px 24px rgba(0,0,0,0.08)',
        cursor: 'pointer',
      }}
    >
      <Blueprint opacity={0.06} />
      {/* Radial glow on hover */}
      <Box sx={{ position: 'absolute', inset: 0, background: hov ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0)', transition: 'background 0.35s ease' }} />

      <Box sx={{ position: 'relative', p: { xs: 3, md: 3.5 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Icon */}
        <Box sx={{
          width: 52, height: 52, borderRadius: '16px',
          bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3,
        }}>
          {Icon && <Icon sx={{ fontSize: 26, color: 'rgba(255,255,255,0.9)' }} />}
        </Box>

        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '1.45rem', lineHeight: 1.2, mb: 1.5 }}>
          {service.title}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.88rem', lineHeight: 1.7, flex: 1 }}>
          {service.description}
        </Typography>

        {/* CTA */}
        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.83rem' }}>Mehr erfahren</Typography>
          <Box sx={{ transition: 'transform 0.3s ease', transform: hov ? 'translateX(5px)' : 'translateX(0)' }}>
            <ArrowForwardIcon sx={{ color: gold, fontSize: 17 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ServicesSection() {
  return (
    <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: '#fff' }}>
      <Container maxWidth="lg">
        <InView style={{ height: 'auto' }}>
          <Box sx={{ mb: { xs: 4, md: 5 } }}>
            <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2em', mb: 0.75 }}>Unsere Leistungen</Typography>
            <Typography variant="h2" sx={{ color: navy, fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
              Was wir für Sie tun
            </Typography>
          </Box>
        </InView>
        <Grid container spacing={2.5}>
          {serviceOverview.map((svc, i) => (
            <Grid size={{ xs: 12, md: 4 }} key={svc.path}>
              <InView variants={fadeUp} delay={i * 0.1} style={{ height: 'auto' }}>
                <ServiceCard service={svc} gradient={svcGradients[i]} />
              </InView>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STATS
// ══════════════════════════════════════════════════════════════════════════════
function StatsSection({ content }) {
  return (
    <Box sx={{
      py: { xs: 7, md: 9 },
      background: `linear-gradient(135deg, ${navy} 0%, ${navyLight} 55%, ${navy} 100%)`,
      position: 'relative', overflow: 'hidden',
    }}>
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '28px 28px',
      }} />
      <Box sx={{ position: 'absolute', top: '-30%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 700, borderRadius: '50%', background: `radial-gradient(circle, rgba(209,188,138,0.05) 0%, transparent 65%)`, filter: 'blur(90px)', pointerEvents: 'none' }} />
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <StatsCounter light items={content?.stats?.items} />
      </Container>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PROCESS
// ══════════════════════════════════════════════════════════════════════════════
function ProcessSection({ steps }) {
  return (
    <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: '#F8F7F5' }}>
      <Container maxWidth="lg">
        <InView style={{ height: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2em', mb: 0.75 }}>Unser Prozess</Typography>
            <Typography variant="h2" sx={{ color: navy, fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.025em' }}>
              In 4 Schritten zum Ziel
            </Typography>
          </Box>
        </InView>

        <Grid container spacing={3}>
          {steps.map((step, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <InView variants={fadeUp} delay={i * 0.1} style={{ height: 'auto' }}>
                <Box sx={{ textAlign: 'center' }}>
                  {/* Number badge with connector line stub */}
                  <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                    <Box sx={{
                      width: 80, height: 80, borderRadius: '24px', mx: 'auto',
                      background: i === 0
                        ? `linear-gradient(135deg, ${gold} 0%, ${goldDark} 100%)`
                        : `rgba(15,29,46,0.06)`,
                      border: i !== 0 ? `1.5px solid rgba(15,29,46,0.1)` : 'none',
                      boxShadow: i === 0 ? `0 14px 36px rgba(209,188,138,0.3)` : 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${gold} 0%, ${goldDark} 100%)`,
                        boxShadow: `0 14px 36px rgba(209,188,138,0.3)`,
                        transform: 'translateY(-4px)',
                        '& .stepNum': { color: `${navy} !important` },
                      },
                    }}>
                      <Typography className="stepNum" sx={{
                        fontWeight: 900, fontSize: '1.7rem', lineHeight: 1,
                        color: i === 0 ? navy : 'rgba(15,29,46,0.22)',
                        transition: 'color 0.3s ease',
                      }}>
                        {step.step}
                      </Typography>
                    </Box>
                    {/* Connector dot to next step */}
                    {i < steps.length - 1 && (
                      <Box sx={{
                        display: { xs: 'none', md: 'block' },
                        position: 'absolute', top: '50%', left: '100%',
                        width: 'calc(100% - 8px)', height: 1.5,
                        background: `linear-gradient(90deg, rgba(209,188,138,0.3) 0%, transparent 100%)`,
                        transform: 'translateY(-50%)',
                        ml: 1,
                      }} />
                    )}
                  </Box>
                  <Typography variant="h6" sx={{ color: navy, fontWeight: 800, mb: 1, fontSize: '1rem' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.72, fontSize: '0.86rem', maxWidth: 200, mx: 'auto' }}>
                    {step.desc}
                  </Typography>
                </Box>
              </InView>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ADVANTAGES / ABOUT TEASER
// ══════════════════════════════════════════════════════════════════════════════
function AdvantagesSection({ advantages, sectionImage, projects = [], heroStats = [], founded }) {
  const imageUrl = sectionImage?.filename
    ? getImageUrl(sectionImage.filename, sectionImage.folder || 'general')
    : null;

  // Use up to 4 projects for the collage fallback
  const collageProjects = projects.slice(0, 4);

  return (
    <Box sx={{ py: { xs: 7, md: 11 }, bgcolor: '#fff', overflow: 'hidden' }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">

          {/* Visual panel */}
          <Grid size={{ xs: 12, md: 5 }}>
            <InView variants={fadeLeft} style={{ height: 'auto' }}>
              <Box sx={{ position: 'relative' }}>
                <Box sx={{
                  borderRadius: '28px', overflow: 'hidden',
                  height: { xs: 280, md: 420 },
                  position: 'relative',
                  background: `linear-gradient(145deg, ${navy} 0%, ${navyLight} 100%)`,
                  boxShadow: '0 20px 60px rgba(15,29,46,0.18)',
                }}>
                  {imageUrl ? (
                    /* Single CMS image */
                    <>
                      <Box sx={{ position: 'absolute', inset: 0, background: `url(${imageUrl}) center/cover no-repeat` }} />
                      <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(15,29,46,0.65) 0%, rgba(15,29,46,0.1) 55%, transparent 100%)' }} />
                    </>
                  ) : collageProjects.length >= 2 ? (
                    /* 2×2 project collage */
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 0.5, position: 'absolute', inset: 0 }}>
                      {[...Array(4)].map((_, i) => {
                        const p = collageProjects[i % collageProjects.length];
                        const pUrl = p ? getProjectImageUrl(p) : null;
                        return (
                          <Box key={i} sx={{
                            background: pUrl ? `url(${pUrl}) center/cover no-repeat` : (p?.gradient || `linear-gradient(135deg, ${navyLight} 0%, #2A4562 100%)`),
                          }} />
                        );
                      })}
                      {/* Unified dark overlay */}
                      <Box sx={{ position: 'absolute', inset: 0, background: 'rgba(10,18,32,0.38)' }} />
                    </Box>
                  ) : (
                    /* Fallback gradient */
                    <>
                      <Blueprint opacity={0.065} />
                      <Box sx={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse 60% 60% at 65% 30%, rgba(209,188,138,0.1) 0%, transparent 65%)` }} />
                    </>
                  )}

                  {/* Bottom glass stats row */}
                  <Box sx={{ position: 'absolute', bottom: 20, left: 20, right: 20, zIndex: 2 }}>
                    <Box sx={{
                      bgcolor: 'rgba(10,18,32,0.7)', backdropFilter: 'blur(16px)',
                      borderRadius: '14px', p: 2, border: `1px solid rgba(209,188,138,0.2)`,
                    }}>
                      <Stack direction="row" justifyContent="space-around">
                        {[
                          [founded || '—', 'Gegründet'],
                          ...(heroStats || []).slice(0, 2).map(s => [`${s.value}${s.suffix}`, s.label]),
                        ].map(([v, l]) => (
                          <Box key={l} sx={{ textAlign: 'center' }}>
                            <Typography sx={{ color: gold, fontWeight: 900, fontSize: '1.3rem', lineHeight: 1 }}>{v}</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', mt: 0.3 }}>{l}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                </Box>
                {/* Floating accent */}
                <Box sx={{
                  position: 'absolute', top: -16, right: -16,
                  px: 2.5, py: 1.8, borderRadius: '16px',
                  background: `linear-gradient(135deg, ${gold} 0%, ${goldDark} 100%)`,
                  boxShadow: `0 14px 36px rgba(209,188,138,0.38)`,
                  '@keyframes floatAccent': { '0%,100%': { transform: 'translateY(0) rotate(-2deg)' }, '50%': { transform: 'translateY(-5px) rotate(-2deg)' } },
                  animation: 'floatAccent 5s ease-in-out infinite',
                }}>
                  <Typography sx={{ color: navy, fontWeight: 900, fontSize: '1.3rem', lineHeight: 1 }}>100%</Typography>
                  <Typography sx={{ color: 'rgba(15,29,46,0.55)', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', mt: 0.3 }}>Zufriedenheit</Typography>
                </Box>
              </Box>
            </InView>
          </Grid>

          {/* Text + list */}
          <Grid size={{ xs: 12, md: 7 }}>
            <InView variants={fadeRight} style={{ height: 'auto' }}>
              <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.2em', mb: 1.5 }}>Warum Süd Ausbau</Typography>
              <Typography variant="h2" sx={{ color: navy, fontWeight: 900, lineHeight: 1.08, letterSpacing: '-0.025em', mb: 2 }}>Ihr Vorteil mit uns</Typography>
              <Typography sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.78, maxWidth: 460, fontSize: '0.98rem' }}>
                Wir vereinen traditionelles Handwerk mit modernen Methoden — für Ergebnisse, die überzeugen und Räume, die begeistern.
              </Typography>
              <Grid container spacing={1} sx={{ mb: 4 }}>
                {advantages.map((adv, i) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={i}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{
                      py: 1.25, px: 1.75, borderRadius: '10px',
                      transition: 'all 0.25s ease',
                      '&:hover': { bgcolor: 'rgba(209,188,138,0.07)', transform: 'translateX(4px)' },
                    }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: 'rgba(184,160,110,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <CheckCircleIcon sx={{ color: goldDark, fontSize: 16 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: navy }}>{adv}</Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
              <Button
                variant="contained" size="large" component={RouterLink} to="/uber-uns"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  background: `linear-gradient(135deg, ${navy} 0%, ${navyLight} 100%)`,
                  color: '#fff', fontWeight: 700, borderRadius: '12px', px: 3.5, py: 1.5,
                  boxShadow: `0 10px 28px rgba(15,29,46,0.18)`,
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 18px 44px rgba(15,29,46,0.26)` },
                  transition: 'all 0.3s ease',
                }}
              >
                Mehr über uns
              </Button>
            </InView>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PAGE ROOT
// ══════════════════════════════════════════════════════════════════════════════
export default function Home() {
  const { projects: apiProjects } = useProjects();
  const { content: homeContent } = usePageContent('home');
  const { settings } = useSettings();
  const projects = apiProjects || staticProjects;
  const processSteps = homeContent?.process?.items || defaultProcessSteps;
  const advantages = homeContent?.advantages?.items || defaultAdvantages;

  // Marquee and hero stats from settings, fallback to hardcoded
  const marqueeItems = (settings?.marquee_items && settings.marquee_items.length > 0)
    ? settings.marquee_items
    : MARQUEE_ITEMS;
  const heroStats = (settings?.hero_stats && settings.hero_stats.length > 0)
    ? settings.hero_stats
    : [{ value: '150', suffix: '+', label: 'Projekte' }, { value: '15', suffix: '+', label: 'Jahre' }, { value: '30', suffix: '+', label: 'Mitarbeiter' }];
  const satisfaction = settings?.satisfaction || '100';

  return (
    <>
      <Helmet>
        <title>Süd Ausbau GmbH — Innenausbau, Umbau &amp; Sanierung München</title>
        <meta name="description" content="Ihr Partner für hochwertigen Innenausbau, Umbau und Sanierung in München und Umgebung. Süd Ausbau GmbH — Qualität aus Meisterhand." />
        <link rel="canonical" href="https://sued-ausbau.de/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sued-ausbau.de/" />
        <meta property="og:title" content="Süd Ausbau GmbH — Innenausbau, Umbau & Sanierung München" />
        <meta property="og:description" content="Ihr Partner für hochwertigen Innenausbau, Umbau und Sanierung in München und Umgebung. Süd Ausbau GmbH — Qualität aus Meisterhand." />
        <meta property="og:image" content="https://sued-ausbau.de/og-preview.png" />
      </Helmet>
      <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <HeroSection projects={projects} heroContent={homeContent?.hero} heroStats={heroStats} satisfaction={satisfaction} />
        <MarqueeStrip items={marqueeItems} />
      </Box>
      <ProjectsSection projects={projects} />
      <ServicesSection />
      <StatsSection content={homeContent} />
      <ProcessSection steps={processSteps} />
      <AdvantagesSection advantages={advantages} sectionImage={homeContent?.advantages?.image} projects={projects} heroStats={heroStats} founded={settings?.company?.founded} />
      <CTABanner pageKey="home" />
    </>
  );
}
